import moment from "moment";
import {
  getRoundKeys,
  getRoundCount,
  computeCompletion,
  touchedAtMs,
} from "../lifecycle";

/**
 * Category labels for a game's mini-board thumbnail. Uses the first configured
 * round (round1, else the lowest round, else final/practice). Caps at 6 so the
 * thumbnail stays legible. Empty when the game has no categories yet.
 */
export const boardCategories = (game, max = 6) => {
  const categories = game?.categories || {};
  const roundKey =
    getRoundKeys(game)[0] ||
    (categories.final ? "final" : categories.practice ? "practice" : null);
  if (!roundKey) return [];
  // The fallback can resolve to `final` (a { category, question } object, not an
  // array), and a mid-generation game can have a non-array round — guard so
  // .map can't throw.
  const cats = Array.isArray(categories[roundKey]) ? categories[roundKey] : [];
  return cats
    .map((c) => (c && c.category ? c.category : ""))
    .slice(0, max);
};

/** "3 rounds · 42/66 clues" — the standard card/hero meta line fragments. */
export const metaParts = (game) => {
  const rounds = getRoundCount(game);
  const { written, total } = computeCompletion(game);
  const cluesLabel =
    total > 0 && written !== total ? `${written}/${total} clues` : `${written} clues`;
  return {
    rounds,
    roundsLabel: `${rounds} ${rounds === 1 ? "round" : "rounds"}`,
    written,
    total,
    cluesLabel,
  };
};

/** Relative "2h ago" for a game's most-recent touch. Empty when unknown. */
export const touchedAgo = (game) => {
  const ms = touchedAtMs(game);
  return ms ? moment(ms).fromNow() : "";
};

/**
 * Launch / resume a game (PRD §5.5): open the projected game board in a new tab
 * (the host drags it to a TV) and take the controller in the current tab.
 * Called from a click handler so the new tab isn't popup-blocked.
 */
export const launchGame = (game, navigate) => {
  if (typeof window !== "undefined" && game?.id) {
    window.open(`/gameboard/${game.id}`, "_blank", "noopener");
  }
  navigate(`/manage/game/${game.id}/controller`);
};

/**
 * Open just the projected game board in a (named, dedupe'd) tab — no controller
 * navigation. Used to view a played game's results, where the board shows the
 * live finale / superlatives. Called from a click handler so it isn't blocked.
 */
export const openBoard = (game) => {
  if (typeof window !== "undefined" && game?.id) {
    window.open(`/gameboard/${game.id}`, `gameboard-${game.id}`);
  }
};
